import { useCallback, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import i18n from '@/utils/i18n';

interface GuardResult {
  confirmed: boolean;
  comment: string;
}

interface OrderChangeGuardDialogProps {
  open: boolean;
  comment: string;
  onCommentChange: (value: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

function OrderChangeGuardDialog({
  open,
  comment,
  onCommentChange,
  onConfirm,
  onCancel,
}: OrderChangeGuardDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{i18n.t('Order:changeGuardTitle')}</DialogTitle>
          <DialogDescription>
            {i18n.t('Order:changeGuardDescription')}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Textarea
            value={comment}
            onChange={(e) => onCommentChange(e.target.value)}
            className="tw:min-h-24 tw:resize-none"
            placeholder={i18n.t('Order:commentPlaceholder')}
          />
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{i18n.t('Common:cancel')}</Button>
          </DialogClose>
          <Button variant="destructive" onClick={onConfirm}>
            {i18n.t('Order:changeGuardConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function useOrderChangeGuard(currentComment: string) {
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState('');
  const resolveRef = useRef<((result: GuardResult) => void) | undefined>(
    undefined,
  );

  const requestConfirmation = useCallback(() => {
    setComment(currentComment);
    setOpen(true);
    return new Promise<GuardResult>((resolve) => {
      resolveRef.current = resolve;
    });
  }, [currentComment]);

  const handleConfirm = useCallback(() => {
    resolveRef.current?.({ confirmed: true, comment });
    setOpen(false);
  }, [comment]);

  const handleCancel = useCallback(() => {
    resolveRef.current?.({ confirmed: false, comment: '' });
    setOpen(false);
  }, []);

  const guardDialog = (
    <OrderChangeGuardDialog
      open={open}
      comment={comment}
      onCommentChange={setComment}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );

  return { requestConfirmation, OrderChangeGuardDialog: guardDialog };
}
